/**
 * Device authorization response from Keycloak
 */
export interface DeviceAuthorizationResponse {
  /** Device code used for polling */
  device_code: string;

  /** User code to display to user */
  user_code: string;

  /** Verification URI (plain) */
  verification_uri: string;

  /** Complete verification URI with user code (for QR codes) */
  verification_uri_complete: string;

  /** Expiration time in seconds */
  expires_in: number;

  /** Recommended polling interval in seconds */
  interval: number;
}

/**
 * Token response from Keycloak
 */
export interface TokenResponse {
  /** Access token */
  access_token: string;

  /** Token type (usually 'Bearer') */
  token_type: string;

  /** Expiration time in seconds */
  expires_in: number;

  /** Refresh token (if offline_access scope was requested) */
  refresh_token?: string;

  /** ID token */
  id_token?: string;

  /** Granted scopes */
  scope?: string;
}

/**
 * Error response from token polling
 */
export interface DeviceFlowError {
  /** Error code */
  error: string;

  /** Error description */
  error_description?: string;
}

/**
 * Request device authorization code from the backend API
 *
 * This function calls your backend API which proxies the request to Keycloak.
 * This avoids CORS issues.
 *
 * @param apiBaseUrl - Base URL of your backend API (e.g., 'http://localhost:5000')
 * @returns Device authorization response
 * @throws Error if request fails
 *
 * @example
 * ```ts
 * const response = await requestDeviceCode('http://localhost:5000');
 * console.log('User code:', response.user_code);
 * console.log('Verification URL:', response.verification_uri_complete);
 * ```
 */
export async function requestDeviceCode(
  apiBaseUrl: string
): Promise<DeviceAuthorizationResponse> {
  const url = `${apiBaseUrl}/api/keycloak/device/authorize`;

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Device authorization failed: ${response.status} ${response.statusText}. ${errorText}`
      );
    }

    const data = await response.json();
    return data as DeviceAuthorizationResponse;
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(`Failed to request device code: ${error.message}`);
    }
    throw error;
  }
}

/**
 * Poll for token using device code
 *
 * This function should be called repeatedly until the user authorizes the device
 * or the device code expires. The backend handles the actual token exchange with Keycloak.
 *
 * @param apiBaseUrl - Base URL of your backend API
 * @param deviceCode - Device code from requestDeviceCode()
 * @returns Token response if authorized, null if still pending
 * @throws Error if request fails or authorization is denied
 *
 * @example
 * ```ts
 * const deviceCode = 'ABC123';
 * const interval = setInterval(async () => {
 *   try {
 *     const token = await pollForToken('http://localhost:5000', deviceCode);
 *     if (token) {
 *       clearInterval(interval);
 *       console.log('Authorized! Token:', token.access_token);
 *     }
 *   } catch (error) {
 *     if (error.message.includes('authorization_pending')) {
 *       // Still waiting, continue polling
 *     } else {
 *       // Real error, stop polling
 *       clearInterval(interval);
 *       console.error('Authorization failed:', error);
 *     }
 *   }
 * }, 5000);
 * ```
 */
export async function pollForToken(
  apiBaseUrl: string,
  deviceCode: string
): Promise<TokenResponse | null> {
  const url = `${apiBaseUrl}/api/keycloak/device/token`;

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ device_code: deviceCode }),
    });

    const data = await response.json();

    // Check if response contains an error (backend returns 200 OK with error object)
    if ('error' in data) {
      const errorData = data as DeviceFlowError;

      // authorization_pending and slow_down are expected during polling
      if (
        errorData.error === 'authorization_pending' ||
        errorData.error === 'slow_down'
      ) {
        return null; // Still waiting
      }

      // Other errors are real failures
      throw new Error(
        errorData.error_description || errorData.error || 'Token polling failed'
      );
    }

    // Check if we got a non-OK status without error in body
    if (!response.ok) {
      throw new Error(`Token polling failed with status ${response.status}`);
    }

    return data as TokenResponse;
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Token polling failed');
  }
}

/**
 * Fetch user information using access token
 *
 * @param apiBaseUrl - Base URL of your backend API
 * @param accessToken - Access token from token response
 * @returns User information
 * @throws Error if request fails
 *
 * @example
 * ```ts
 * const userInfo = await fetchUserInfo('http://localhost:5000', token.access_token);
 * console.log('User:', userInfo.preferred_username);
 * ```
 */
export async function fetchUserInfo(
  apiBaseUrl: string,
  accessToken: string
): Promise<any> {
  const url = `${apiBaseUrl}/api/keycloak/device/userinfo`;

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ access_token: accessToken }),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch user info: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(`Failed to fetch user info: ${error.message}`);
    }
    throw error;
  }
}
